import { createApp } from "vue";
import "vuetify/styles";
import { createVuetify } from "vuetify";
import { aliases, mdi } from "vuetify/iconsets/mdi";
import "@mdi/font/css/materialdesignicons.css";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { createI18n } from "vue-i18n";
import router from "./router";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import App from "./App.vue";
import translations from "./translations/translations";
import { chorusTheme } from "./lib/ui/chorus.theme";
import LicenseSelect from "./components/LicenseSelect.vue";

const vuetify = createVuetify({
  components: {
    ...components,
    LicenseSelect,
  },
  directives,
  theme: {
    defaultTheme: "chorus",
    themes: {
      chorus: chorusTheme
    }
  },
  icons: {
    defaultSet: "mdi",
    aliases,
    sets: {
      mdi
    }
  },
  aliases: {
    VBtnSecondary: components.VBtn,
    VBtnTertiary: components.VBtn,
    VBtnSubtle: components.VBtn,
    VBtnNavFooter: components.VBtn,
    VBtnLink: components.VBtn,
    VDataCard: components.VCard,
    VTextFieldSearch: components.VTextField,
    VModalCard: components.VCard,
    VImgIcon: components.VImg,
  },
  defaults: {
    VBtnLink: {
      variant: "text",
      density: "compact",
      slim: true,
      color: "anchor",
      appendIcon: "mdi-chevron-right",
      style: {
        textTransform: "none",
      }
    },
    VBtn: {
      color: "btn-primary",
      variant: "flat",
      elevation: 2,
      style: {
        textTransform: "none",
      }
    },
    VBtnSecondary: {
      color: "btn-secondary",
      variant: "outlined",
      elevation: 2,
      style: {
        textTransform: "none",
      }
    },
    VBtnTertiary: {
      variant: "text",
      color: "btn-tonal",
      baseColor: "btn-icon",
      elevation: 0,
      style: {
        textTransform: "none",
        borderRadius: "4px"
      }
    },
    VBtnSubtle: {
      color: "subtle",
      variant: "flat",
      elevation: 0,
      border: "sm",
      style: {
        textTransform: "none",
        borderColor: "rgb(var(--v-theme-subtle-border)) !important",
        boxShadow: "0px 1px 15px -15px rgba(var(--v-theme-on-subtle), 0.1) !important"
      }
    },
    VBtnNavFooter: {
      color: "footer-link-text-color",
      variant: "plain",
      elevation: 0,
      class: "opacity-100 text-decoration-underline text-capitalize",
    },
    VCard: {
      border: "thin",
      elevation: 0,
      // Set variant for buttons inside card and card actions
      VBtn: { variant: "flat" },
      VCardTitle: {
        class: "text-body-1 text-card"
      },
      VCardSubtitle: {
        class: "opacity-100 text-card-subtitle"
      },
      VCardText: {
        class: "text-card",
        VDataTable: {
          style: {
            border: "1px solid",
            borderRadius: "4px",
            borderColor: "rgb(var(--v-theme-subtle-border)) !important"
          }
        }
      }
    },
    VDataCard: {
      border: "thin",
      elevation: 0,
      VBtn: { variant: "flat" },
      VCardTitle: {
        class: "text-body-1 d-flex justify-space-between align-center pa-5 text-title",
        style: {
          height: "76px"
        }
      },
      VCard: {
        VCardTitle: {
          class: "text-body-1",
          style: {
            height: "auto"
          }
        }
      }
    },
    VCardActions: {
      VBtn: { variant: "flat" },
    },
    VTextField: {
      variant: "outlined",
      density: "compact",
      style: "font-size: 0.875rem !important"
    },
    VField: {
      style: "font-size: 0.875rem !important"
    },
    VLabel: {
      style: "font-size: 0.875rem !important"
    },
    VAutocomplete: {
      variant: "outlined",
      density: "compact",
    },
    VTextarea: {
      variant: "outlined",
    },
    VSelect: {
      variant: "outlined",
      density: "compact",
    },
    VCombobox: {
      variant: "outlined",
      density: "compact",
    },
    VTextFieldSearch: {
      variant: "outlined",
      density: "comfortable",
      hideDetails: "auto",
      clearable: true,
      appendInnerIcon: "mdi-magnify",
      style: {
        backgroundColor: "rgb(var(--v-theme-surface))"
      }
    },
    VDataTable: {
      elevation: 0,
      density: "default",
      headerProps: {
        style: {
          fontWeight: 600
        }
      }
    },
    VDataTableServer: {
      elevation: 0,
      density: "compact",
      showSelect: true,
      headerProps: {
        style: {
          fontWeight: 600
        }
      }
    },
    VPagination: {
      VBtnTertiary: {
        width: "28px",
        height: "28px"
      }
    },
    VModalCard: {
      class: "pa-0",
      VCardTitle: {
        class: "px-8 pt-6 text-body-1 mb-1"
      },
      VCardItem: {
        class: "pa-0"
      },
      VForm: {
        class: "pt-0 px-8 pb-6",
        VCardText: {
          class: "pa-0"
        },
        VCardActions: {
          style: {
            "min-height": "36px"
          },
          class: "d-flex justify-center pa-0 pt-4"
        }
      }
    },
    VDialog: {
      VForm: {
        style: {
          backgroundColor: "rgb(var(--v-theme-surface-variant)) !important",
        }
      }
    },
    VImgIcon: {
      class: "v-icon v-icon--size-default"
    },
    VCheckbox: {
      color: "success",
      class: "v-chorus-checkbox"
    },
    VCheckboxBtn: {
      color: "success",
      class: "v-chorus-checkbox"
    }
  },
});

export const i18n = createI18n({
  legacy: false,
  locale: "en-us",
  fallbackLocale: "en-us",
  messages: translations,
  datetimeFormats: {
    "en-us": {
      short: {
        year: "numeric",
        month: "short",
        day: "numeric"
      },
      long: {
        year: "numeric",
        month: "short",
        day: "numeric",
        weekday: "short",
        hour: "numeric",
        minute: "numeric"
      }
    }
  },
  numberFormats: {
    "en-us": {
      millisecond: { style: "unit", unit: "millisecond", unitDisplay: "narrow" },
      second: { style: "unit", unit: "second", unitDisplay: "narrow" },
      minute: { style: "unit", unit: "minute", unitDisplay: "narrow" },
      hour: { style: "unit", unit: "hour", unitDisplay: "narrow" }
    }
  }
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App);
app.use(router);
app.use(pinia);
app.use(vuetify);
app.use(i18n);

router.isReady().then(() => {
  app.mount("#app");
});
